/**
 * Demo-mode master switch. Gates the MLOps, Serverless/GPU Dashboard,
 * Notebook, and Baremetal surfaces. When off (default), those surfaces are
 * hidden in the UI and their backend APIs return 403.
 */
export const isDemoEnabled = (): boolean => {
  return process.env.REACT_APP_IS_DEMO === 'true';
};

/**
 * Baremetal is gated by demo mode (no dedicated flag existed before).
 */
export const isBaremetalEnabled = (): boolean => {
  return isDemoEnabled();
};

/**
 * Check if AI Studio feature is enabled
 */
export const isAIStudioEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_AI_STUDIO === 'true';
};

/**
 * Check if VM Manager feature is enabled
 */
export const isVMManagerEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_VM_MANAGER === 'true';
};

/**
 * Check if MLOps (Experiment Tracking & Model Registry) feature is enabled (demo-gated)
 */
export const isMLOpsEnabled = (): boolean => isDemoEnabled();

/**
 * Check if Model Registry feature is enabled within MLOps (demo-gated)
 */
export const isModelRegistryEnabled = (): boolean => isDemoEnabled();

/**
 * Check if Experiments feature is enabled within MLOps (demo-gated)
 */
export const isExperimentsEnabled = (): boolean => isDemoEnabled();

/**
 * Check if Quick Notes feature is enabled
 */
export const isQuickNotesEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_QUICK_NOTES === 'true';
};

/**
 * Check if Inference feature is enabled
 */
export const isInferenceEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_INFERENCE === 'true';
};

/**
 * Check if Reactive Notebooks feature is enabled (demo-gated)
 */
export const isReactiveNotebooksEnabled = (): boolean => isDemoEnabled();

/**
 * Check if real-time notebook collaboration is enabled
 */
export const isCollaborationEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_COLLABORATION === 'true';
};

/**
 * Check if ML Pipelines feature is enabled (demo-gated)
 */
export const isMLPipelinesEnabled = (): boolean => isDemoEnabled();

/**
 * Check if Model Monitoring feature is enabled (demo-gated)
 */
export const isModelMonitoringEnabled = (): boolean => isDemoEnabled();

/**
 * Check if Drift Detection feature is enabled (demo-gated)
 */
export const isDriftDetectionEnabled = (): boolean => isDemoEnabled();

/**
 * Check if KYC enforcement is enabled
 * When enabled, shows KYC banner and blocks provisioning for unverified users
 */
export const isKYCEnforcementEnabled = (): boolean => {
  return process.env.REACT_APP_KYC_ENFORCEMENT_ENABLED !== 'false'; // Default is enabled
};

/**
 * Check if Sandbox feature is enabled
 */
export const isSandboxEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_SANDBOX === 'true';
};

/**
 * Check if Podvirt Serverless Inference feature is enabled (demo-gated)
 */
export const isPodvirtInferenceEnabled = (): boolean => isDemoEnabled();

/**
 * Check if MinIO/TrueNAS-backed File Storage is enabled.
 * Default-on so existing builds keep the page; set
 * REACT_APP_ENABLE_FILE_STORAGE=false at build time to remove it.
 */
export const isFileStorageEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_FILE_STORAGE !== 'false';
};

/**
 * Check if Managed MLOps (managed MLflow/Kubeflow Pipelines/DVC/Metaflow) is enabled.
 * Default off until the backend ships.
 */
export const isManagedMLOpsEnabled = (): boolean => {
  return process.env.REACT_APP_MANAGED_MLOPS_ENABLED === 'true';
};

/**
 * Check if the FinOps section (AWS connect, cost dashboard, migration
 * recommendations) is enabled. Default off until finops-service is deployed.
 */
export const isFinOpsEnabled = (): boolean => {
  return process.env.REACT_APP_FINOPS_ENABLED === 'true';
};

